// The one table. TanStack Table for the column model, TanStack Virtual for
// long lists, shadcn's table pieces for the look.
//
// It never pages, sorts or filters on its own: every list in this product is
// a window onto millions of rows, so the server does all three and the page
// owns the request. The table takes the rows it is given and a `footer` for
// the page's own paging controls (`<TablePager>`), which keeps each page's
// cursor or page-number logic exactly where it was written.
//
// Past VIRTUAL_FROM rows (a contact list after a few "Továbbiak") only the
// rows in view are in the DOM, inside a scroll box with a sticky head.

import { Fragment, useRef } from "react";
import type { KeyboardEvent, ReactNode } from "react";
import { getCoreRowModel, useReactTable } from "@tanstack/react-table";
import type { ColumnDef, Row, RowData } from "@tanstack/react-table";
import { useVirtualizer } from "@tanstack/react-virtual";
import {
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Skeleton } from "@/components/ui/skeleton";
import { cn } from "@/lib/utils";

declare module "@tanstack/react-table" {
  // The type parameters must match TanStack's own declaration to merge.
  interface ColumnMeta<TData extends RowData, TValue> {
    /** Numbers right, so their digits line up. */
    align?: "left" | "right" | "center";
    /** Tabular figures: codes, times, counts. (Real code — an id, an IP —
     * sets `font-mono` in its cell.) */
    mono?: boolean;
    /** Secondary text colour. */
    muted?: boolean;
    /** Let long text wrap instead of keeping the row on one line. */
    wrap?: boolean;
    className?: string;
  }
}

const VIRTUAL_FROM = 150;

// A column's header or cell, called as a plain function. TanStack's
// flexRender mounts a function as a component, and a page that builds its
// columns during render (to close over its own state) hands over a new
// function every time — React would then remount every cell on every
// render, and an input inside a cell would lose focus after each keystroke.
// Cells here hold no hooks, so a plain call is all they need.
function renderTemplate<P>(template: unknown, props: P): ReactNode {
  if (typeof template === "function") return (template as (props: P) => ReactNode)(props);
  return (template as ReactNode) ?? null;
}
// h-10 rows: the estimate only seeds the virtualiser, each row is measured.
const ROW_ESTIMATE = 40;
const LOADING_ROWS = 5;

export interface DataTableProps<T> {
  // TanStack's column type is invariant in the value type; `any` here is
  // what lets one array hold a string column next to a number column.
  columns: ColumnDef<T, any>[];
  data: T[];
  getRowId?: (row: T, index: number) => string;
  /** Makes rows clickable (and reachable with Tab + Enter). */
  onRowClick?: (row: T) => void;
  /** The row the page has open. */
  isRowSelected?: (row: T) => boolean;
  rowClassName?: (row: T) => string | undefined;
  /** A full-width row under a row — the page decides when it is open and
   * returns null otherwise. Tables with it are never virtualised. */
  renderExpanded?: (row: T) => ReactNode;
  /** Shown instead of the body when `data` is empty and not loading. */
  empty?: ReactNode;
  /** With no rows yet: skeleton rows. With rows: the rows stay. */
  loading?: boolean;
  /** Under the rows, inside the frame: paging, counts. */
  footer?: ReactNode;
  /** "card" stands alone on a page; "plain" sits inside a card. */
  variant?: "card" | "plain";
  /** Force virtualisation on or off; by default it starts at VIRTUAL_FROM rows. */
  virtualize?: boolean;
  /** For screen readers: what the rows are. */
  label?: string;
  className?: string;
}

function alignClass(align: "left" | "right" | "center" | undefined): string | undefined {
  if (align === "right") return "text-right";
  if (align === "center") return "text-center";
  return undefined;
}

export function DataTable<T>({
  columns,
  data,
  getRowId,
  onRowClick,
  isRowSelected,
  rowClassName,
  renderExpanded,
  empty,
  loading = false,
  footer,
  variant = "card",
  virtualize,
  label,
  className,
}: DataTableProps<T>) {
  const table = useReactTable({
    data,
    columns,
    getRowId,
    getCoreRowModel: getCoreRowModel(),
    manualPagination: true,
    manualSorting: true,
    manualFiltering: true,
  });

  const rows = table.getRowModel().rows;
  const columnCount = table.getVisibleLeafColumns().length;
  const virtual = (virtualize ?? rows.length >= VIRTUAL_FROM) && renderExpanded === undefined;

  const scrollRef = useRef<HTMLDivElement>(null);
  const virtualizer = useVirtualizer({
    count: virtual ? rows.length : 0,
    getScrollElement: () => scrollRef.current,
    estimateSize: () => ROW_ESTIMATE,
    overscan: 12,
  });

  function renderRow(row: Row<T>, measure?: { index: number }) {
    const original = row.original;
    const selected = isRowSelected?.(original) ?? false;
    const clickable = onRowClick !== undefined;
    const onKeyDown = clickable
      ? (event: KeyboardEvent<HTMLTableRowElement>) => {
          if (event.target !== event.currentTarget) return;
          if (event.key === "Enter" || event.key === " ") {
            event.preventDefault();
            onRowClick(original);
          }
        }
      : undefined;
    const expanded = renderExpanded?.(original) ?? null;
    return (
      <Fragment key={row.id}>
        <TableRow
          data-state={selected ? "selected" : undefined}
          data-index={measure?.index}
          ref={measure ? virtualizer.measureElement : undefined}
          tabIndex={clickable ? 0 : undefined}
          onClick={clickable ? () => onRowClick(original) : undefined}
          onKeyDown={onKeyDown}
          className={cn(
            clickable && "cursor-pointer",
            expanded !== null && "border-b-0 bg-fill-secondary",
            rowClassName?.(original),
          )}
        >
          {row.getVisibleCells().map((cell) => {
            const meta = cell.column.columnDef.meta;
            return (
              <TableCell
                key={cell.id}
                className={cn(
                  !meta?.wrap && "whitespace-nowrap",
                  alignClass(meta?.align),
                  meta?.mono && "data-mono",
                  meta?.muted && "text-muted-foreground",
                  meta?.className,
                )}
              >
                {renderTemplate(cell.column.columnDef.cell, cell.getContext())}
              </TableCell>
            );
          })}
        </TableRow>
        {expanded !== null && (
          <TableRow className="bg-fill-secondary hover:bg-fill-secondary">
            <TableCell colSpan={columnCount} className="pt-0 pb-4 whitespace-normal">
              {expanded}
            </TableCell>
          </TableRow>
        )}
      </Fragment>
    );
  }

  let body: ReactNode;
  if (rows.length === 0 && loading) {
    body = Array.from({ length: LOADING_ROWS }, (_, index) => (
      <TableRow key={`loading-${index}`}>
        {Array.from({ length: columnCount }, (__, cell) => (
          <TableCell key={cell}>
            <Skeleton className="h-3.5 w-3/4" />
          </TableCell>
        ))}
      </TableRow>
    ));
  } else if (rows.length === 0) {
    body = (
      <TableRow className="hover:bg-transparent">
        <TableCell colSpan={columnCount} className="p-0 whitespace-normal">
          {empty ?? <p className="px-4 py-8 text-center text-muted-foreground">Nincs sor.</p>}
        </TableCell>
      </TableRow>
    );
  } else if (virtual) {
    const items = virtualizer.getVirtualItems();
    const top = items[0]?.start ?? 0;
    const bottom = virtualizer.getTotalSize() - (items[items.length - 1]?.end ?? 0);
    body = (
      <>
        {top > 0 && (
          <tr aria-hidden="true">
            <td colSpan={columnCount} style={{ height: top }} />
          </tr>
        )}
        {items.map((item) => {
          const row = rows[item.index];
          return row === undefined ? null : renderRow(row, { index: item.index });
        })}
        {bottom > 0 && (
          <tr aria-hidden="true">
            <td colSpan={columnCount} style={{ height: bottom }} />
          </tr>
        )}
      </>
    );
  } else {
    body = rows.map((row) => renderRow(row));
  }

  return (
    <div
      data-slot="data-table"
      className={cn(
        "flex min-w-0 flex-col",
        variant === "card" && "overflow-hidden rounded-card bg-card shadow-card",
        className,
      )}
    >
      <div
        ref={scrollRef}
        className={cn("relative w-full overflow-x-auto", virtual && "max-h-[70vh] overflow-y-auto")}
      >
        <table aria-label={label} className="w-full border-collapse text-body">
          <TableHeader className={cn(virtual && "sticky top-0 z-10 bg-card")}>
            {table.getHeaderGroups().map((group) => (
              <TableRow key={group.id} className="hover:bg-transparent">
                {group.headers.map((header) => {
                  const meta = header.column.columnDef.meta;
                  return (
                    <TableHead key={header.id} className={alignClass(meta?.align)}>
                      {header.isPlaceholder
                        ? null
                        : renderTemplate(header.column.columnDef.header, header.getContext())}
                    </TableHead>
                  );
                })}
              </TableRow>
            ))}
          </TableHeader>
          <TableBody>{body}</TableBody>
        </table>
      </div>
      {footer && (
        <div className="border-t border-separator px-5.5 py-3 text-detail" data-slot="data-table-footer">
          {footer}
        </div>
      )}
    </div>
  );
}

/** The footer row for server-side paging: a count on the left, the page's
 * own buttons (with the page's own handlers) on the right. */
export function TablePager({ summary, children }: { summary?: ReactNode; children?: ReactNode }) {
  return (
    <div className="flex flex-wrap items-center justify-between gap-3">
      <span className="text-muted-foreground">{summary}</span>
      <div className="flex items-center gap-2 [&_[data-slot=button]]:h-control-sm">{children}</div>
    </div>
  );
}
